import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Form } from 'react-form';
import moment from 'moment';

import { Input, DateTimePicker, Location } from '../../components/Form';
import { saveEvent } from '../../actions/event-actions';
import { showErrorPopup } from '../../actions/notification-actions';
import { required } from '../../utils/form-validation';
import { deleteEvent } from '../../api/tmsEventsApi';
import { getApiAxiosInstance } from '../../utils/network';
import {
    dateFormat,
    durationOptions,
    getFreePlayButtonTextForState,
    getEventEndDate,
    getIdForType,
    getNewEmailInvites,
    getNewMemberInvites,
    getFreePlayPopupContentForState,
    mapEmailInvites,
    mapMembers,
    permissionTypes,
    validateDate,
    mapOrganizer,
} from '../../utils/createPlay';
import { ClubTierTypes } from '../../utils/clubs';
import { EventTypes } from '../../utils/events';
import Select from '../../components/Form/Select';
import PlayerInvite from '../../components/PlayerInvite';
import PlayerInviteModal from '../../components/Modals/PlayerInviteModal';
import styles from './createPlayPage.scss';
import GenericPopup from '../../components/PopUps/GenericPopup';
import GenericPopupWithTwoButtons from '../../components/PopUps/GenericPopup/GenericPopupWithTwoButtons';
import Img from '../../components/Img';
import { sportTypeIds, maxTitleLength } from '../../utils/eventConstants';
import { useStatsig } from '../../components/hooks/useStatsig';

const CreatePlayPage = (props) => {
    const { auth, coreUI, events, isEditingEvent, match } = props;
    const { competitionTypes, pickleBallCompetitionTypes, gameTypes, sportTypes } = coreUI?.types?.tms || {};
    const { user } = auth;
    const { isSaving } = events.currentEvent || {};
    const organizer = mapOrganizer(user);
    const popupContent = getFreePlayPopupContentForState(isEditingEvent);
    const [isFinishedLoading, setIsFinishedLoading] = useState(false);
    const [savedEventId, setSavedEventId] = useState(null);
    const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [showCancelPopup, setShowCancelPopup] = useState(false);
    const [isCancelingEvent, setIsCancelingEvent] = useState(false);
    const [previousInvitees, setPreviousInvitees] = useState({ members: [], nonMembers: [] });
    const [originalGroupMembers, setOriginalGroupMembers] = useState([]);
    const [originalOpponentMembers, setOriginalOpponentMembers] = useState([]);
    const [removedGroupMemberId, setRemovedGroupMemberId] = useState('');
    const [removedOpponentMemberId, setRemovedOpponentMemberId] = useState('');
    const [eventData, setEventData] = useState({
        name: `${user.firstName} ${user.lastName}'s Game`,
        eventPermissionType: 2,
        details: '',
        eventSchedule: {
            eventStartUtc: moment(),
        },
        eventLocations: [{
            location: '',
            eventLocationType: '1',
        }],
        eventDuration: 1,
        eventType: EventTypes.FREE_PLAY,
        gameType: 2,
        competitionType: 3,
        memberInvites: [],
        emailInvites: [],
        sportTypeId: sportTypeIds.TENNIS,
    });

    useEffect(() => {
        const { eventId } = match?.params || {};
        const currentEvent = events?.currentEvent?.data;

        if (isEditingEvent && currentEvent.id === parseInt(eventId)) {
            const { eventSchedule, eventPermissionType, eventLocations, invites, name, eventSettings } = currentEvent;
            const startDate = moment.utc(eventSchedule.eventStartUtc).local();
            const endDate = moment.utc(eventSchedule.eventEndUtc).local();
            const duration = endDate.diff(startDate, 'hours', true);
            const permissionTypeId = eventPermissionType.id;
            const memberInvites = mapMembers(invites.members);
            const emailInvites = mapEmailInvites(invites.nonMembers);
            const location = eventLocations[0].googleFormattedName;
            const notes = currentEvent.details;
            const competitionType = getIdForType((currentEvent?.sportTypeId === sportTypeIds.PICKLEBALL ? pickleBallCompetitionTypes : competitionTypes), eventSettings.competitionType);
            const gameType = eventSettings?.gameTypeId;
            const sportTypeId = currentEvent?.sportTypeId;

            setPreviousInvitees({ members: memberInvites, nonMembers: emailInvites });
            setEventData({
                name,
                eventPermissionType: permissionTypeId,
                details: notes,
                eventSchedule: {
                    eventStartUtc: startDate,
                },
                eventLocations: [{
                    location,
                    eventLocationType: '1',
                }],
                eventDuration: duration,
                eventType: EventTypes.FREE_PLAY,
                memberInvites,
                emailInvites,
                gameType,
                competitionType,
                sportTypeId,
            });
            setIsFinishedLoading(true);
        } else {
            setIsFinishedLoading(true);
        }
    }, []);

    const handleOnSubmitForm = async (formValues) => {
        const event = { ...formValues };
        const { emailInvites } = eventData;
        const memberInvites = eventData.memberInvites.map(member => { return { memberId: member.source.memberId }; });
        const invitees = [...memberInvites, ...emailInvites];
        const momentTimeZone = moment.tz.guess();
        const currentTimeZone = momentTimeZone ? coreUI?.types?.timeZones?.find(timeZone => timeZone.value?.toLowerCase() === momentTimeZone.toLowerCase()) : null;
        const defaultTimeZone = currentTimeZone ? currentTimeZone.id : '77';
        const freePlayClub = user?.clubMemberships?.find(membership => membership.tierTypeId === ClubTierTypes.CREATE_PLAY);
        const gameType = parseInt(event.gameType);
        const competitionType = parseInt(event.competitionType);
        const sportTypeId = parseInt(event.sportTypeId);
        const permissionType = parseInt(event.eventPermissionType);
        const eventDirector = [{ memberId: auth.user.id, eventDirectorRole: 2 }];
        const endDate = getEventEndDate(event.eventSchedule.eventStartUtc, event.eventDuration);

        event.eventSchedule.eventEndUtc = endDate;
        event.eventSchedule.timezone = defaultTimeZone;
        event.eventDirectors = eventDirector;
        event.gameType = gameType;
        event.competitionType = competitionType;
        event.sportTypeId = sportTypeId;
        event.eventPermissionType = permissionType;
        event.club = freePlayClub.clubId;
        delete event.eventDuration;
        delete event.emailInvites;
        delete event.memberInvites;

        if (isEditingEvent) {
            const { eventId } = match?.params || {};
            const newMemberInvites = getNewMemberInvites(previousInvitees.members, memberInvites);
            const newEmailInvites = getNewEmailInvites(previousInvitees.nonMembers, emailInvites);
            event.id = eventId;
            event.playersList = [...newMemberInvites, ...newEmailInvites];
        } else {
            event.playersList = invitees;
        }

        const savedEvent = await props.saveEvent(event);
        if (savedEvent) {
            setSavedEventId(savedEvent.id);
            setShowSuccessPopup(true);
        }
    };

    const handleOnDateChange = (date) => {
        setEventData({ ...eventData, eventSchedule: { eventStartUtc: date } });
    };

    const handleOnRemovePlayerFromInvite = (playerId) => {
        const updatedPlayers = eventData.memberInvites.filter(player => player.source.id !== playerId);
        const isGroupMember = originalGroupMembers.some(member => member.source.id === playerId);
        const isOpponentMember = originalOpponentMembers.some(member => member.source.id === playerId);

        if (isGroupMember) {
            setRemovedGroupMemberId(playerId);
        }
        if (isOpponentMember) {
            setRemovedOpponentMemberId(playerId);
        }
        setEventData({ ...eventData, memberInvites: updatedPlayers });
    };

    const handleOnRemoveEmailFromInvite = (email) => {
        const updatedPlayers = eventData.emailInvites.filter(player => player.email !== email);
        setEventData({ ...eventData, emailInvites: updatedPlayers });
    };

    const handleOnInvitePlayerClicked = () => {
        setIsInviteModalVisible(true);
    };

    const handleOnRequestModalClose = () => {
        setIsInviteModalVisible(false);
    };

    const handleOnApplyClicked = (memberInvites, emailInvites) => {
        setEventData({ ...eventData, memberInvites, emailInvites });
    };

    const handleOnSuccessPopupRequestClose = () => {
        const splashMessage = isEditingEvent ? 'Your event has been updated!' : 'Your event has been created!';
        setShowSuccessPopup(false);
        props.history.push({
            pathname: `/events/${savedEventId}`,
            state: { adminSplash: splashMessage },
        });
    };

    const handleOnSuccessPopupShare = () => {
        setShowSuccessPopup(false);
        props.history.push({
            pathname: `/events/${savedEventId}`,
            state: { showSharePopup: true },
        });
    };

    const handleOnCancelPopupRequestClose = () => {
        setShowCancelPopup(false);
    };

    const handleOnCancelEventClicked = () => {
        setShowCancelPopup(true);
    };

    const handleOnCancellationConfirmed = async () => {
        const { eventId } = match?.params || {};
        setShowCancelPopup(false);
        setIsCancelingEvent(true);

        try {
            const response = await deleteEvent(getApiAxiosInstance(), eventId);
            if (response?.data.status === 'success') {
                setIsCancelingEvent(false);
                props.history.push({
                    pathname: '/home',
                    state: { adminSplash: 'Your event has been canceled' },
                });
                return;
            }
            setIsCancelingEvent(false);
            props.showErrorPopup({
                message: 'Error canceling event',
            });
        } catch (error) {
            setIsCancelingEvent(false);
            props.showErrorPopup({
                message: 'Error canceling event',
                error,
            });
        }
    };

    const handleOnCloseEditPage = () => {
        const { eventId } = props.match.params;
        if (eventId) {
            props.history.push(`/events/${eventId}`);
        } else {
            props.history.goBack();
        }
    };

    const handleOnGroupMembersSet = (members) => {
        setOriginalGroupMembers(members);
    };

    const handleOnOpponentMembersSet = (members) => {
        setOriginalOpponentMembers(members);
    };

    const renderCancelPopup = () => {
        const { eventSchedule, name } = eventData;
        const eventDate = eventSchedule.eventStartUtc.format('ll');
        const eventTime = eventSchedule.eventStartUtc.format('LT');
        const title = 'Cancel Event';
        const subtitle = `Are you sure you want to cancel ${name} on ${eventDate} at ${eventTime}?`;

        return (
            <GenericPopup
                title={title}
                subtitle={subtitle}
                buttonText={title}
                onRequestClose={handleOnCancelPopupRequestClose}
                onButtonClick={handleOnCancellationConfirmed}
                data-testid="modal.create-play.cancel-confirmation"
            />
        );
    };

    const getCompetitionTypes = (formApi) => {
        const { values } = formApi;
        if (parseInt(values.sportTypeId) === sportTypeIds.PICKLEBALL) {
            return pickleBallCompetitionTypes;
        }
        return competitionTypes;
    };
    const { checkGate, featureGates } = useStatsig();
    const isEventCharacterCountEnabled = checkGate(featureGates.WEB_EVENT_CHARACTER_COUNT);

    return (
        <>
            <div className="navbar navbar-expand-md navbar-light mt0 sticky-top">
                <div className="container" style={{ position: 'relative' }}>
                    <div className="row w-100">
                        <div className="col d-flex aic">
                            <div className="close-area" onClick={handleOnCloseEditPage} data-testid="create-play.page.close-btn">
                                <Img className="close-icon" src="/img/icons/close-icon-lg.svg" data-testid="create-play.page.close-icon" />
                            </div>
                            <div className="d-inline d-md-none title-span text-medium text-uppercase text-dark text-center" data-testid="create-play.page.mobile-title">
                                {isEditingEvent ? 'Edit' : 'Create'} Play
                            </div>
                        </div>
                        <div className="col d-none d-md-flex aic jcc">
                            <div className="title-span text-medium text-uppercase text-dark text-center" data-testid="create-play.page.desktop-title">
                                {isEditingEvent ? 'Edit' : 'Create'} Play
                            </div>
                        </div>
                        <div className="col d-flex aic jce" />
                    </div>
                </div>
            </div>
            <div className="container">
                <div className={'row justify-content-center mt48'}>
                    {isFinishedLoading && (
                        <Form onSubmit={handleOnSubmitForm} defaultValues={{ ...eventData }}>
                            {(formApi) => {
                                const { submitForm, values } = formApi;
                                const charCount = (values.name || '').length;

                                return (
                                    <div className={styles.inputsContainer}>
                                        <form onSubmit={submitForm} id="createPlayForm" className="mt64" data-testid="create-play.page.form">
                                            <Input
                                                id="name"
                                                field="name"
                                                label="Name"
                                                hiddenLabel={false}
                                                validate={required}
                                                maxLength={isEventCharacterCountEnabled ? maxTitleLength : null}
                                                hoverText={(
                                                    <>
                                                        <strong className="fs16">Event Title Guidelines</strong>
                                                        <p className="mt8">Please follow these guidelines when creating your event titles.</p>
                                                        <ul>
                                                            <li>Event titles must be 75 characters or less (including spaces and punctuation).</li>
                                                        </ul>
                                                    </>
                                                )}
                                                data-testid="create-play.page.name-input"
                                            />
                                            {isEventCharacterCountEnabled && (
                                                <div className="text-right small text-dark mt-12">
                                                    {`Characters: ${charCount}/`}<span className="text-dark text-bold">{maxTitleLength}</span>
                                                </div>
                                            )}
                                            <Select
                                                id="sportTypeId"
                                                label="Sport Type"
                                                field="sportTypeId"
                                                hiddenDefault={true}
                                                options={sportTypes}
                                                data-testid="create-play.page.sport-type-select"
                                            />
                                            <Select
                                                id="gameType"
                                                label="Game Type"
                                                field="gameType"
                                                hiddenDefault={true}
                                                options={gameTypes}
                                                data-testid="create-play.page.game-type-select"
                                            />
                                            <DateTimePicker
                                                label="Date and Time"
                                                id="eventStart"
                                                field={['eventSchedule', 'eventStartUtc']}
                                                selected={eventData.eventSchedule.eventStartUtc}
                                                minDate={moment()}
                                                maxDate={null}
                                                handleDateChange={handleOnDateChange}
                                                hiddenLabel={false}
                                                dateFormat={dateFormat}
                                                validate={validateDate}
                                            />
                                            <Select
                                                id="duration"
                                                label="Duration"
                                                field="eventDuration"
                                                hiddenDefault={true}
                                                options={durationOptions}
                                                data-testid="create-play.page.duration-select"
                                            />
                                            <Location
                                                label="Event Location"
                                                placeholder="Location (enter zip, city or full address)"
                                                id="location"
                                                field={['eventLocations', 0, 'location']}
                                                inputProps={{ name: 'location' }}
                                                useDynamicOption={true}
                                                showMap={false}
                                                validate={required}
                                            />
                                            <Select
                                                id="competitionType"
                                                label="Play Type"
                                                field="competitionType"
                                                options={getCompetitionTypes(formApi)}
                                                hiddenDefault={true}
                                                data-testid="create-play.page.competition-type-select"
                                            />
                                            <Select
                                                id="privacy"
                                                label="Visibility"
                                                field="eventPermissionType"
                                                hiddenDefault={true}
                                                options={permissionTypes}
                                                data-testid="create-play.page.privacy-select"
                                            />
                                            <PlayerInvite
                                                isEditingEvent={isEditingEvent}
                                                invitedMembers={eventData.memberInvites}
                                                invitedEmails={eventData.emailInvites}
                                                previousInvites={previousInvitees}
                                                organizer={organizer}
                                                onRemovePlayer={handleOnRemovePlayerFromInvite}
                                                onRemoveEmail={handleOnRemoveEmailFromInvite}
                                                onInvitePlayerPressed={handleOnInvitePlayerClicked}
                                                isCreatePlay={true}
                                                sportTypeId={parseInt(formApi?.values?.sportTypeId)}
                                            />
                                            <div className="mt64">
                                                <Input
                                                    id="notes"
                                                    field="details"
                                                    label="Add Note (Optional)"
                                                    hiddenLabel={false}
                                                    data-testid="create-play.page.notes-input"
                                                />
                                            </div>
                                            <button
                                                type="submit"
                                                className="btn btn-primary btn-xl text-uppercase btn-block"
                                                disabled={isSaving}
                                                data-testid="create-play.page.submit-btn"
                                            >
                                                {getFreePlayButtonTextForState(isEditingEvent, isSaving)}
                                            </button>
                                            {isEditingEvent && (
                                                <button
                                                    onClick={handleOnCancelEventClicked}
                                                    type="button"
                                                    className={`btn btn-primary btn-xl text-uppercase btn-block ${styles.cancelButton}`}
                                                    disabled={isCancelingEvent}
                                                    data-testid="create-play.page.cancel-event-btn"
                                                >
                                                    {isCancelingEvent ? 'Canceling...' : 'Cancel Event'}
                                                </button>
                                            )}
                                        </form>
                                        <PlayerInviteModal
                                            isVisible={isInviteModalVisible}
                                            invitedMembers={eventData.memberInvites}
                                            invitedEmails={eventData.emailInvites}
                                            removedGroupMemberId={removedGroupMemberId}
                                            removedOpponentMemberId={removedOpponentMemberId}
                                            onRequestClose={handleOnRequestModalClose}
                                            onApplyClicked={handleOnApplyClicked}
                                            onGroupMembersSet={handleOnGroupMembersSet}
                                            onOpponentMembersSet={handleOnOpponentMembersSet}
                                            isCreatePlay={true}
                                            sportTypeId={parseInt(formApi?.values?.sportTypeId)}
                                        />
                                        {showSuccessPopup && (
                                            <GenericPopupWithTwoButtons
                                                data-testid="modal.create-play.success"
                                                title={popupContent.title}
                                                subtitle={popupContent.subtitle}
                                                imageSource="/img/icons/checkmark-teal.svg"
                                                primaryButtonText="OK"
                                                secondaryButtonText={<span><i className="uts uts-share" />Share</span>}
                                                secondaryButtonType="btn-primary"
                                                onRequestClose={handleOnSuccessPopupRequestClose}
                                                onPrimaryButtonClick={handleOnSuccessPopupRequestClose}
                                                onSecondaryButtonClick={handleOnSuccessPopupShare}
                                            />
                                        )}
                                        {showCancelPopup && (renderCancelPopup())}
                                    </div>
                                );
                            }}
                        </Form>
                    )}

                </div>
            </div>
        </>
    );
};

function mapDispatchToProps(dispatch) {
    return {
        saveEvent: (data) => dispatch(saveEvent(data)),
        showErrorPopup: (data) => dispatch(showErrorPopup(data)),
    };
}

function mapStateToProps({ auth, club, coreUI, events }) {
    return { auth, club, coreUI, events };
}

export default connect(mapStateToProps, mapDispatchToProps)(CreatePlayPage);
